import {
  Button,
  Card,
  Eyebrow,
  PageHeader,
  SelectField,
  StatusGlyph,
  StatusPill,
  cn,
} from '@homecare/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { ErrorNote } from '../../components/primitives';
import { useApi, useRefreshClients } from '../../lib/api-hooks';

/**
 * Import a roster (§7, §5).
 *
 * Upload → mapping → preview with errors inline → confirm → progress → result
 * with the error CSV.
 *
 * ## The preview is not a formality
 *
 * §5 says never import-then-report, and the screen is shaped to make the
 * preview the place a decision happens rather than a page to click past. The
 * confirm button says how many rows will land and how many will not, so
 * pressing it is a choice about a number rather than an acknowledgement.
 *
 * ## Mapping is editable before anything is written
 *
 * A new export never matches our headers, and the first thing somebody does is
 * fix the matching. Changing a column here re-previews against the same
 * uploaded file — no re-upload — because making them find the file again is
 * what turns a five-minute job into an afternoon.
 */

const FIELD_LABELS: Record<string, string> = {
  mrn: 'Client number',
  firstName: 'First name',
  middleName: 'Middle name',
  lastName: 'Last name',
  preferredName: 'Goes by',
  dateOfBirth: 'Date of birth',
  gender: 'Gender',
  ssnLast4: 'SSN last 4',
  medicaidId: 'Medicaid ID',
  medicareId: 'Medicare ID',
  primaryLanguage: 'Primary language',
};

const REQUIRED = new Set(['mrn', 'firstName', 'lastName', 'dateOfBirth']);

interface RowError {
  row: number;
  column: string;
  value: string;
  message: string;
}

interface Preview {
  jobId: string;
  filename: string;
  sourceHeaders: string[];
  mapping: Record<string, string>;
  mappingRemembered: boolean;
  mappingProblems: string[];
  totalRows: number;
  validRows: number;
  failedRows: number;
  errors: RowError[];
  errorsTruncated: boolean;
}

interface JobResult {
  id: string;
  filename?: string;
  createdAt?: string;
  status: string;
  totalRows: number;
  createdRows: number;
  updatedRows: number;
  failedRows: number;
  errors: RowError[];
  errorCsvUrl: string | null;
}

export function ClientImport(): ReactNode {
  const api = useApi();
  const refreshClients = useRefreshClients();
  const queryClient = useQueryClient();

  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [result, setResult] = useState<JobResult | null>(null);
  const [busy, setBusy] = useState<'uploading' | 'importing' | null>(null);
  const [failure, setFailure] = useState<unknown>(null);

  async function uploadFile(chosen: File, mapping?: Record<string, string>): Promise<void> {
    setFailure(null);
    setBusy('uploading');

    try {
      const fields = mapping ? { mapping: JSON.stringify(mapping) } : {};
      setPreview(await api.upload<Preview>('/v1/clients/import/preview', chosen, fields));
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  }

  async function confirm(): Promise<void> {
    if (!preview) return;

    setFailure(null);
    setBusy('importing');

    try {
      await api.send('POST', `/v1/clients/import/${preview.jobId}/confirm`, {
        rememberMapping: true,
      });

      // The run is a background job, so the result is polled rather than
      // returned. It finishes in about a second for 200 rows; this is the
      // honest shape rather than a spinner that lies about being synchronous.
      let job: JobResult | null = null;

      for (let attempt = 0; attempt < 60; attempt += 1) {
        job = await api.get<JobResult>(`/v1/clients/import/${preview.jobId}`);
        if (job.status === 'COMPLETED' || job.status === 'FAILED') break;
        await new Promise((done) => setTimeout(done, 500));
      }

      await refreshClients();
      await queryClient.invalidateQueries({ queryKey: ['clients', 'imports'] });
      setResult(job);
      setPreview(null);
    } catch (error) {
      setFailure(error);
    } finally {
      setBusy(null);
    }
  }

  async function downloadErrors(): Promise<void> {
    if (!result?.errorCsvUrl) return;

    const { blob, filename } = await api.download(result.errorCsvUrl);
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();

    URL.revokeObjectURL(url);
  }

  return (
    <div className="grid gap-5">
      <PageHeader
        title="Import clients"
        meta="Upload your existing roster as a CSV. Nothing is saved until you confirm."
      />

      <ErrorNote error={failure} nextStep="Check the file and try again." />

      {result ? (
        <Result result={result} onDownload={downloadErrors} onAgain={() => setResult(null)} />
      ) : preview ? (
        <PreviewStep
          preview={preview}
          busy={busy}
          onRemap={(mapping) => {
            if (file) void uploadFile(file, mapping);
          }}
          onConfirm={() => void confirm()}
          onCancel={() => setPreview(null)}
        />
      ) : (
        <>
          <UploadStep
            busy={busy}
            onChoose={(chosen) => {
              setFile(chosen);
              void uploadFile(chosen);
            }}
          />
          <EarlierImports onOpen={setResult} />
        </>
      )}
    </div>
  );
}

function UploadStep({
  busy,
  onChoose,
}: {
  busy: 'uploading' | 'importing' | null;
  onChoose: (file: File) => void;
}): ReactNode {
  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Choose a file</h2>
      <p className="text-ink-2 mt-2 max-w-prose text-sm">
        A CSV with the column names in the first row. Whatever your current system exports is
        usually right — you match the columns up on the next screen, and nothing is written until
        you have seen what will happen.
      </p>

      <label className="mt-5 block">
        <span className="text-ink-2 text-sm font-semibold">CSV file</span>
        <input
          type="file"
          accept=".csv,text/csv"
          disabled={busy !== null}
          onChange={(event) => {
            const chosen = event.target.files?.[0];
            if (chosen) onChoose(chosen);
          }}
          className="text-ink-2 file:bg-surface-2 file:text-ink file:border-line mt-2 block w-full text-sm file:mr-4 file:rounded-md file:border file:px-4 file:py-2 file:text-sm file:font-semibold"
        />
      </label>

      {busy === 'uploading' && (
        <p className="text-ink-3 mt-4 text-sm">Reading the file and checking every row…</p>
      )}
    </Card>
  );
}

function PreviewStep({
  preview,
  busy,
  onRemap,
  onConfirm,
  onCancel,
}: {
  preview: Preview;
  busy: 'uploading' | 'importing' | null;
  onRemap: (mapping: Record<string, string>) => void;
  onConfirm: () => void;
  onCancel: () => void;
}): ReactNode {
  const blocked = preview.mappingProblems.length > 0;

  return (
    <div className="grid gap-5">
      <Card>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-ink text-lg font-bold">Match the columns</h2>
            <p className="text-ink-3 mt-1 text-sm">
              {preview.filename} · {preview.totalRows} rows
              {preview.mappingRemembered && ' · matched the way you did last time'}
            </p>
          </div>
          <Button variant="secondary" onClick={onCancel}>
            Choose a different file
          </Button>
        </div>

        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {Object.keys(FIELD_LABELS).map((field) => (
            <SelectField
              key={field}
              label={`${FIELD_LABELS[field]}${REQUIRED.has(field) ? ' *' : ''}`}
              value={preview.mapping[field] ?? ''}
              onChange={(event) =>
                onRemap({ ...preview.mapping, [field]: event.target.value || undefined } as Record<
                  string,
                  string
                >)
              }
            >
              <option value="">Not matched</option>
              {preview.sourceHeaders.map((header) => (
                <option key={header} value={header}>
                  {header}
                </option>
              ))}
            </SelectField>
          ))}
        </div>

        {blocked && (
          <ul className="border-warning-br bg-warning-bg text-warning mt-5 grid gap-2 rounded-md border p-4 text-sm">
            {preview.mappingProblems.map((problem) => (
              <li key={problem} className="flex items-start gap-2">
                <StatusGlyph name="triangle" className="mt-0.5 h-4 w-4 flex-none" />
                {problem}
              </li>
            ))}
          </ul>
        )}
      </Card>

      {!blocked && (
        <Card>
          <h2 className="text-ink text-lg font-bold">What will happen</h2>

          <div className="mt-4 flex flex-wrap gap-3">
            <StatusPill tone="verified">{rowCount(preview.validRows)} will import</StatusPill>
            {preview.failedRows > 0 && (
              <StatusPill tone="warning">{rowCount(preview.failedRows)} will not</StatusPill>
            )}
          </div>

          {preview.errors.length > 0 && (
            <div className="mt-5">
              <Eyebrow>What is wrong, row by row</Eyebrow>
              <ul className="border-line mt-2 grid divide-y rounded-md border">
                {preview.errors.map((error, position) => (
                  <li
                    key={`${error.row}-${error.column}-${position}`}
                    className="px-4 py-3 text-sm"
                  >
                    <span className="text-ink font-medium">
                      Row {error.row}, {error.column}
                    </span>
                    <span className="text-ink-2">: {error.message}</span>
                  </li>
                ))}
              </ul>
              {preview.errorsTruncated && (
                <p className="text-ink-3 mt-2 text-xs">
                  Showing the first {preview.errors.length}. The rest are in the error file you can
                  download after importing.
                </p>
              )}
            </div>
          )}

          <div className="border-line mt-5 flex flex-wrap items-center gap-3 border-t pt-5">
            <Button onClick={onConfirm} pending={busy === 'importing'}>
              {preview.failedRows > 0
                ? `Import ${rowCount(preview.validRows)}, skip ${preview.failedRows}`
                : `Import ${rowCount(preview.validRows)}`}
            </Button>
            <p className="text-ink-3 text-sm">
              {preview.failedRows > 0
                ? 'The rows that fail come back as a file to fix and re-upload.'
                : 'Every row checks out.'}
            </p>
          </div>
        </Card>
      )}
    </div>
  );
}

function Result({
  result,
  onDownload,
  onAgain,
}: {
  result: JobResult;
  onDownload: () => void;
  onAgain: () => void;
}): ReactNode {
  const failed = result.failedRows > 0;

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">
        {result.status === 'FAILED'
          ? 'The import did not finish'
          : result.status === 'COMPLETED'
            ? 'Import finished'
            : 'Still importing'}
      </h2>
      {result.filename && (
        <p className="text-ink-3 mt-1 text-sm">
          {result.filename}
          {result.createdAt ? ` · started ${when(result.createdAt)}` : ''}
        </p>
      )}
      {result.status !== 'FAILED' && result.status !== 'COMPLETED' && (
        <p className="text-ink-2 mt-2 text-sm">
          It is still running. It carries on without this page — find it under Earlier imports to
          see how it finished.
        </p>
      )}

      <dl className="mt-4 grid gap-4 sm:grid-cols-3">
        <Measure label="Added" value={result.createdRows} tone="verified" />
        <Measure label="Updated" value={result.updatedRows} tone="progress" />
        <Measure
          label="Not imported"
          value={result.failedRows}
          tone={failed ? 'warning' : 'inactive'}
        />
      </dl>

      {result.updatedRows > 0 && (
        <p className="text-ink-2 mt-4 text-sm">
          Rows matching a client number you already had updated that client rather than adding a
          second one.
        </p>
      )}

      <div className="border-line mt-5 flex flex-wrap items-center gap-3 border-t pt-5">
        {failed && result.errorCsvUrl && (
          <Button onClick={onDownload}>
            Download the {result.failedRows === 1 ? 'row' : `${result.failedRows} rows`} that failed
          </Button>
        )}
        <Button variant="secondary" onClick={onAgain}>
          Import another file
        </Button>
      </div>

      {failed && (
        <p className="text-ink-3 mt-3 text-sm">
          The file has your own columns plus one saying what was wrong. Fix those rows and upload it
          again — the ones that already imported will update rather than duplicate.
        </p>
      )}
    </Card>
  );
}

const rowCount = (n: number): string => (n === 1 ? '1 row' : `${n} rows`);

const when = (iso: string): string =>
  new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );

const STATUS_TONE: Record<string, 'verified' | 'progress' | 'critical'> = {
  COMPLETED: 'verified',
  QUEUED: 'progress',
  RUNNING: 'progress',
  FAILED: 'critical',
};

const STATUS_LABEL: Record<string, string> = {
  COMPLETED: 'Finished',
  QUEUED: 'Waiting to start',
  RUNNING: 'Importing',
  FAILED: 'Did not finish',
};

/**
 * Imports this agency has run, newest first — `GET /v1/clients/import`.
 *
 * Until this existed an import's result lived only on the screen that started
 * it: leave the page, or let the poll above give up on a long file, and the
 * counts and the failed-rows CSV were unreachable from the console. The route
 * was there since slice 1 and nothing called it; the reachability gate only
 * stopped mistaking the page's own path for a call in slice 2 session 9.
 *
 * A preview that was never confirmed is not an import, so it is not listed.
 */
function EarlierImports({ onOpen }: { onOpen: (job: JobResult) => void }): ReactNode {
  const api = useApi();
  const jobs = useQuery({
    queryKey: ['clients', 'imports'],
    queryFn: () => api.get<{ jobs: JobResult[] }>('/v1/clients/import'),
    staleTime: 0,
  });

  const ran = (jobs.data?.jobs ?? []).filter((job) => job.status !== 'PREVIEWED');

  return (
    <Card>
      <h2 className="text-ink text-lg font-bold">Earlier imports</h2>
      <ErrorNote error={jobs.error} nextStep="Reload the page to try again." />
      {jobs.isPending ? (
        <p className="text-ink-3 mt-2 text-sm">Loading…</p>
      ) : ran.length === 0 ? (
        <p className="text-ink-3 mt-2 text-sm">No file has been imported yet.</p>
      ) : (
        <ul className="divide-line mt-3 grid divide-y" aria-label="Earlier imports">
          {ran.map((job) => (
            <li key={job.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
              <div className="grid gap-0.5">
                <span className="text-ink text-sm font-medium">{job.filename}</span>
                <span className="text-ink-3 text-xs" data-numeric>
                  {job.createdAt ? when(job.createdAt) : ''} · {job.createdRows} added ·{' '}
                  {job.updatedRows} updated · {job.failedRows} not imported
                </span>
              </div>
              <div className="flex items-center gap-3">
                <StatusPill tone={STATUS_TONE[job.status] ?? 'progress'}>
                  {STATUS_LABEL[job.status] ?? job.status}
                </StatusPill>
                <Button variant="secondary" size="sm" onClick={() => onOpen(job)}>
                  View result
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Measure({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone: 'verified' | 'progress' | 'warning' | 'inactive';
}): ReactNode {
  const colour = {
    verified: 'text-verified',
    progress: 'text-progress',
    warning: 'text-warning',
    inactive: 'text-ink-3',
  }[tone];

  return (
    <div>
      <dt className="eyebrow">{label}</dt>
      <dd className={cn('text-metric mt-1 font-bold leading-none', colour)} data-numeric>
        {value}
      </dd>
    </div>
  );
}
